import { useEffect, useRef, useState } from "react";

import { Input } from "antd";
import {
  MAX_LINE_HALF_LENGTH_METERS,
  MIN_LINE_HALF_LENGTH_METERS,
  clampLineHalfLengthMeters
} from "stores/wellCardPanel";
import styled from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { useLengthConversion } from "hooks/useUnitConversion";

/** What one arrow click moves, in whatever unit the box is showing. */
const DISPLAY_STEP = 100;
/**
 * The precision that leaves `convert` unrounded. The box shows twice the metres
 * it is given, and the doubling has to be done on the unrounded conversion: a
 * width rounded to the whole unit first and doubled after drops the odd unit
 * every time the box reads its own number back, so 4,001 ft returns as 4,000.
 */
const UNROUNDED = -1;
/**
 * How long a stepped value waits before it is committed. An arrow leaves no
 * other sign that the number is finished, and every committed value re-derives
 * the section line over two HTTP calls, so a run of clicks applies once.
 */
const STEP_COMMIT_DELAY_MS = 500;

/**
 * Why the control is dead, and the way out of it. A drawn line is the user's
 * own, so the length has nothing to govern until the card derives a line
 * again, and it derives one for each well it is pointed at.
 */
const DISABLED_HINT =
  "A drawn line is in use. Open the card on another well for the automatic line.";

export interface HalfWidthControlProps {
  /** Half the section's span, which is what the line is derived from. */
  halfLengthMeters: number;
  /** Answers in the same half-length metres, not in the shown span. */
  onChange: (meters: number) => void;
  /** The control governs the auto-derived line only. */
  disabled: boolean;
}

/**
 * The width of the cross-section: the whole span, centred on the well, which is
 * the distance the user reads off the section rather than the half-length the
 * derive works in. Metres in and metres out are still half-lengths, so the
 * store, the derive and any XDA widget sharing the line see no change; the
 * doubling lives here and nowhere else.
 *
 * Built to match the shapefile filter's Buffer row, which is welded to its own
 * feature (`ShapefileFieldOptions.tsx`) rather than shared: the same label
 * type and greying, the same small antd number box at 30px, and the browser's
 * own arrows, so the number steps the way it does everywhere else in EVA.
 */
export function HalfWidthControl({
  halfLengthMeters,
  onChange,
  disabled
}: HalfWidthControlProps): JSX.Element {
  const { convert, convertBack, getDisplayUnit } = useLengthConversion();

  // Exactly what has been typed, character for character, for as long as the
  // box has focus. Nothing tidies it and nothing caps it: a number rewritten
  // while it is half typed cannot be finished, and in Imperial the round trip
  // through metres moved the digits as well.
  const [typing, setTyping] = useState<Maybe<string>>(null);
  const focusedRef = useRef(false);
  // Whether the change now arriving came from the arrows rather than the
  // keyboard. The arrows leave a finished number, so it commits on its own;
  // a typed digit waits for Enter or for the box to be left.
  const pointerRef = useRef(false);
  const stepKeyRef = useRef(false);

  const settledValue = Math.round(convert(halfLengthMeters, UNROUNDED) * 2);
  const displayUnit = getDisplayUnit();

  // Halved back to the length the line is derived from before anything else
  // touches it. Clamped here and nowhere else, in metre space, which is what
  // stops an Imperial user stepping in feet from crossing the cap. An empty or
  // unreadable box is not a request for the default width, so it commits
  // nothing and the settled value comes back on its own.
  const commit = (text: string) => {
    if (disabled) return;
    const typed = parseFloat(text);
    if (!Number.isFinite(typed)) return;
    onChange(clampLineHalfLengthMeters(convertBack(typed / 2)));
  };

  const commitStep = useDebouncedCallback(commit, STEP_COMMIT_DELAY_MS);

  const settle = (text: string) => {
    commitStep.cancel();
    commit(text);
    setTyping(null);
  };

  // The settled value is what the box shows once it is left. While it has
  // focus this does nothing: syncing there is what used to replace a
  // half-typed number with the clamped, round-tripped one.
  useEffect(() => {
    if (focusedRef.current) return;
    setTyping(null);
  }, [halfLengthMeters]);

  // Leaving the tab must not throw away a step the arrows have not applied yet.
  useEffect(() => () => commitStep.flush(), [commitStep]);

  // The metre clamps on the span the box shows, so they are doubled with it.
  // Rounded inward to a whole step: an outward round would let the arrows, or
  // a typed number, sit past what map-svc accepts and come back a 400 the
  // panel cannot tell apart from a well with no survey.
  const displayMax =
    Math.floor((convert(MAX_LINE_HALF_LENGTH_METERS, UNROUNDED) * 2) / DISPLAY_STEP) *
    DISPLAY_STEP;
  const displayMin =
    Math.ceil((convert(MIN_LINE_HALF_LENGTH_METERS, UNROUNDED) * 2) / DISPLAY_STEP) *
    DISPLAY_STEP;

  return (
    // The hint sits on the wrapper as well as the input: a disabled input takes
    // no pointer events in most browsers, so its own title never shows - which
    // is exactly the state the user needs telling about.
    <Wrapper
      data-testid="well-card-half-width"
      title={disabled ? DISABLED_HINT : undefined}>
      <Label $dead={disabled}>Length</Label>
      <WidthInput
        aria-label="Cross-section length"
        disabled={disabled}
        type="number"
        min={displayMin}
        max={displayMax}
        step={DISPLAY_STEP}
        size="small"
        value={typing ?? settledValue}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onMouseDown={() => {
          pointerRef.current = true;
        }}
        onKeyDown={(event) => {
          pointerRef.current = false;
          stepKeyRef.current = event.key === "ArrowUp" || event.key === "ArrowDown";
        }}
        onChange={(event) => {
          const text = event.target.value;
          setTyping(text);
          if (pointerRef.current || stepKeyRef.current) {
            commitStep(text);
          }
        }}
        onBlur={(event) => {
          focusedRef.current = false;
          settle(event.target.value);
        }}
        onPressEnter={(event) => settle(event.currentTarget.value)}
        title={disabled ? DISABLED_HINT : "Total cross-section length"}
      />
      <Unit $dead={disabled}>{displayUnit}</Unit>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex: none;
`;

// Greys out with the box beside it, so the whole control reads as one thing
// that is not available rather than as a live label over a dead box.
const Label = styled.span<{ $dead: boolean }>`
  font-size: 1.4rem;
  color: ${(props) => (props.$dead ? "var(--color-text-20)" : "var(--color-text-60)")};
`;

const WidthInput = styled(Input)`
  width: 92px;
  height: 30px;
`;

const Unit = styled.span<{ $dead: boolean }>`
  font-size: 1.2rem;
  color: ${(props) => (props.$dead ? "var(--color-text-20)" : "var(--color-text-60)")};
`;
